import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { type ClassValue } from "clsx"

import { cn } from "../../lib/utils"

const iconButtonVariants = cva(
  "inline-flex items-center justify-center rounded font-semibold font-body outline-none disabled:cursor-not-allowed pb-[0.0625rem] active:pb-0 active:pt-[0.0625rem] flat:!shadow-none flat:py-0 flat:active:py-0",
  {
    variants: {
      variant: {
        primary:
          "bg-gradient-to-b to-primary-600  from-primary-500 shadow-inner shadow-primary-700 text-white hover:from-primary-600 hover:to-primary-700 hover:shadow-primary-800 active:shadow-sm active:from-primary-800 active:to-primary-700  active:shadow-primary-800 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:text-gray-400 disabled:bg-gray-200 disabled:bg-none disabled:shadow-gray-300",
        secondary:
          "bg-gradient-to-b to-gray-50 from-white shadow-inner shadow-gray-100 text-gray-700 border border-gray-200 hover:from-gray-50 hover:to-gray-100 hover:shadow-gray-200 hover:border-gray-300 hover:text-primary-600 active:shadow-sm active:from-gray-200 active:to-gray-100  active:shadow-gray-200 focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 disabled:text-gray-400 disabled:bg-gray-100 disabled:bg-none disabled:shadow-gray-200 disabled:border-gray-300",
        secondaryRound:
          "rounded-xl bg-gradient-to-b to-gray-50 from-white shadow-inner shadow-gray-100 text-gray-700 border border-gray-200 hover:from-gray-50 hover:to-gray-100 hover:shadow-gray-200 hover:border-gray-300 hover:text-primary-600 active:shadow-sm active:from-gray-200 active:to-gray-100  active:shadow-gray-200 focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 disabled:text-gray-400 disabled:bg-gray-100 disabled:bg-none disabled:shadow-gray-200 disabled:border-gray-300",
        tertiary:
          "bg-transparent border border-transparent text-gray-700 hover:bg-gradient-to-b hover:from-white hover:to-gray-50 hover:shadow-inner hover:shadow-gray-100 hover:border-gray-200 hover:text-primary-600 active:shadow-sm active:bg-gradient-to-b active:from-gray-100 active:to-gray-50 active:shadow-gray-100 active:border-gray-200 focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 disabled:text-gray-300 disabled:shadow-none disabled:border-none disabled:bg-none",
        destructive:
          "bg-gradient-to-b to-error-50 from-white shadow-inner shadow-error-100 text-error-500 border border-error-200 hover:from-error-50 hover:to-error-100 hover:shadow-error-200 hover:border-error-500 hover:text-error-600 active:shadow-sm active:from-error-200 active:to-error-100 active:text-error-700 active:border-error-600 active:shadow-error-200 focus-visible:ring-2 focus-visible:ring-error-500 focus-visible:ring-offset-2 disabled:text-gray-400 disabled:bg-gray-100 disabled:bg-none disabled:shadow-gray-200 disabled:border-gray-300",
      },
      size: {
        sm: "px-2.5 h-9 text-sm [&_*]:size-4",
        md: "px-3 h-10 text-md [&_*]:size-4",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
)

interface IconButtonPropsBase
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof iconButtonVariants> {
  icon: React.ReactNode
  iconClassName?: ClassValue
}
export type IconButtonProps = Omit<IconButtonPropsBase, "children"> &
  Required<{
    "aria-label": string
  }>

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      className,
      variant,
      size,
      type = "button",
      icon,
      "aria-label": ariaLabel,
      iconClassName,
      ...props
    },
    ref
  ) => {
    const Comp = "button"
    return (
      <Comp
        className={cn(iconButtonVariants({ variant, size, className }))}
        ref={ref}
        aria-label={ariaLabel}
        type={type}
        {...props}
      >
        <span className={cn("flex justify-center", iconClassName)}>{icon}</span>
      </Comp>
    )
  }
)
IconButton.displayName = "IconButton"

export { IconButton, iconButtonVariants }
